import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Target, Users, TrendingUp, Shield, Zap, CheckCircle, Sparkles, Rocket, Eye, Heart, Cpu, Database, Compass, BarChart3, Activity, Layers } from 'lucide-react';
import { aboutContent, companyInfo } from '../data/mock';

// 1. QUANT LAB CURVED DISPLAY BACKGROUND (Used in Top Hero Background of "About Quanentry Capital")
const QuantLabCurvedBackground = () => {
  const canvasRef = useRef(null);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId;
    let width = 0;
    let height = 0;
    let dpr = 1;

    const updateDimensions = () => {
      if (!canvas.parentElement) return;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.parentElement.clientWidth || 1200;
      height = canvas.parentElement.clientHeight || 750;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.scale(dpr, dpr);
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);

    const COLOR_EMERALD = '#00ffd1';
    const COLOR_AQUA = '#00e5ff';
    const COLOR_PURPLE = '#a855f7';
    const COLOR_WHITE = '#ffffff';

    // Particle nodes for AI Neural Synapses
    const PARTICLE_COUNT = 80;
    const particles = [];
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      particles.push({
        x: Math.random() * (width || 1200),
        y: Math.random() * (height || 750),
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        radius: Math.random() * 2 + 1,
        alpha: Math.random() * 0.6 + 0.4,
        pulseSpeed: 0.02 + Math.random() * 0.03,
        pulseAngle: Math.random() * Math.PI * 2
      });
    }

    // Mathematical Formulas & Machine Learning Concepts
    const mathAndAiFormulas = [
      { text: '∫ f(x)dx', category: 'MATH' },
      { text: 'd²P/dt²', category: 'QUANT' },
      { text: '∇Loss = -η ∂L/∂W', category: 'AI_ML' },
      { text: 'dS = μS dt + σS dW', category: 'STOCHASTIC' },
      { text: 'Sharpe = 3.84', category: 'ALPHA' },
      { text: 'Σ (x-μ)²', category: 'MATH' },
      { text: 'σ² = 0.04', category: 'QUANT' },
      { text: 'L_MSE = ||y - y_hat||²', category: 'AI_ML' },
      { text: 'E[R_p] = r_f + β(E[R_m] - r_f)', category: 'QUANT' },
      { text: 'Softmax(QKᵀ / √d_k)V', category: 'TRANSFORMER' },
      { text: 'P(A|B) = P(B|A)P(A)/P(B)', category: 'BAYES' },
      { text: 'Q(s,a) ← r + γ max Q(s\',a\')', category: 'RL' },
      { text: 'Cov(X,Y) / (σ_X σ_Y)', category: 'QUANT' },
      { text: 'N(d1) - K e^(-rt) N(d2)', category: 'BLACK_SCHOLES' }
    ];

    const aiTechBadges = [
      '[NEURAL NETWORKS: ACTIVE]',
      '[DEEP TRANSFORMER V4.2]',
      '[REINFORCEMENT LEARNING Q-AGENT]',
      '[ML ATTENTION WEIGHTS: OPTIMIZED]',
      '[ORDER FLOW ALPHA ENGINE]',
      '[STOCHASTIC VOLATILITY SKEW]'
    ];

    let time = 0;

    const render = () => {
      time += 0.012;
      ctx.clearRect(0, 0, width, height);

      const cx = width / 2;
      const cy = height / 2;

      // 1. Deep Space Curved Screen Base
      ctx.fillStyle = '#01050a';
      ctx.fillRect(0, 0, width, height);

      // Radial Ambient Lighting
      const bgGrad = ctx.createRadialGradient(
        cx, cy, 30,
        cx, cy, Math.max(width, height) * 0.85
      );
      bgGrad.addColorStop(0, 'rgba(0, 255, 209, 0.16)');
      bgGrad.addColorStop(0.4, 'rgba(0, 229, 255, 0.06)');
      bgGrad.addColorStop(1, 'rgba(1, 5, 10, 0)');
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, width, height);

      // 12-second Multi-stage Synthesis Loop
      const LOOP_DURATION = 12;
      const cycleProgress = (time % LOOP_DURATION) / LOOP_DURATION;

      // 2. CURVED SCREEN GLASS ARCHITECTURE (3D Perspective Grid & Arcs)
      ctx.save();
      ctx.lineWidth = 1;

      // Curved Screen Horizon Arcs
      const curveHeight = 35;
      ctx.strokeStyle = 'rgba(0, 255, 209, 0.15)';
      ctx.beginPath();
      ctx.moveTo(0, 40);
      ctx.quadraticCurveTo(cx, 40 + curveHeight, width, 40);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(0, height - 40);
      ctx.quadraticCurveTo(cx, height - 40 - curveHeight, width, height - 40);
      ctx.stroke();

      // Curved Perspective Vertical Grid Lines
      const gridCols = 14;
      for (let i = 0; i <= gridCols; i++) {
        const xPos = (width / gridCols) * i;
        const curveOffset = Math.sin((i / gridCols) * Math.PI) * 20;
        ctx.strokeStyle = 'rgba(0, 229, 255, 0.06)';
        ctx.beginPath();
        ctx.moveTo(xPos, 40 + curveOffset * 0.5);
        ctx.lineTo(xPos, height - 40 - curveOffset * 0.5);
        ctx.stroke();
      }

      // Curved Glass Outer Screen Frame Glowing Corners
      ctx.strokeStyle = 'rgba(0, 255, 209, 0.35)';
      ctx.lineWidth = 1.5;
      // Top Left Corner
      ctx.beginPath();
      ctx.moveTo(20, 70); ctx.lineTo(20, 30); ctx.lineTo(70, 30); ctx.stroke();
      // Top Right Corner
      ctx.beginPath();
      ctx.moveTo(width - 20, 70); ctx.lineTo(width - 20, 30); ctx.lineTo(width - 70, 30); ctx.stroke();
      // Bottom Left Corner
      ctx.beginPath();
      ctx.moveTo(20, height - 70); ctx.lineTo(20, height - 30); ctx.lineTo(70, height - 30); ctx.stroke();
      // Bottom Right Corner
      ctx.beginPath();
      ctx.moveTo(width - 20, height - 70); ctx.lineTo(width - 20, height - 30); ctx.lineTo(width - 70, height - 30); ctx.stroke();

      ctx.restore();

      // 3. AI & MACHINE LEARNING NEURAL NETWORK LAYERS (Background Left & Right)
      ctx.save();
      const aiAlpha = cycleProgress < 0.90 ? 0.48 : 0.22;
      ctx.globalAlpha = aiAlpha;

      // Layer 1: Neural Input Nodes
      const layer1X = width * 0.08;
      const layer2X = width * 0.22;
      const layer3X = width * 0.36;

      const nodeYs1 = [height * 0.25, height * 0.40, height * 0.55, height * 0.70];
      const nodeYs2 = [height * 0.20, height * 0.35, height * 0.50, height * 0.65, height * 0.80];
      const nodeYs3 = [height * 0.30, height * 0.50, height * 0.70];

      // Synapse Connections between Layer 1 & Layer 2
      nodeYs1.forEach((y1, i1) => {
        nodeYs2.forEach((y2, i2) => {
          ctx.beginPath();
          ctx.moveTo(layer1X, y1);
          ctx.lineTo(layer2X, y2);
          const pulse = Math.sin(time * 3 + i1 + i2) * 0.5 + 0.5;
          ctx.strokeStyle = pulse > 0.6 ? COLOR_EMERALD : 'rgba(0, 229, 255, 0.2)';
          ctx.lineWidth = pulse > 0.6 ? 1.5 : 0.7;
          ctx.stroke();
        });
      });

      // Synapse Connections between Layer 2 & Layer 3
      nodeYs2.forEach((y2, i2) => {
        nodeYs3.forEach((y3, i3) => {
          ctx.beginPath();
          ctx.moveTo(layer2X, y2);
          ctx.lineTo(layer3X, y3);
          const pulse = Math.sin(time * 4 + i2 + i3) * 0.5 + 0.5;
          ctx.strokeStyle = pulse > 0.5 ? COLOR_AQUA : 'rgba(0, 255, 209, 0.2)';
          ctx.lineWidth = pulse > 0.5 ? 1.5 : 0.7;
          ctx.stroke();
        });
      });

      // Draw Neural Nodes
      nodeYs1.forEach(y => {
        ctx.beginPath(); ctx.arc(layer1X, y, 4, 0, Math.PI * 2); ctx.fillStyle = COLOR_EMERALD; ctx.fill();
      });
      nodeYs2.forEach(y => {
        ctx.beginPath(); ctx.arc(layer2X, y, 5, 0, Math.PI * 2); ctx.fillStyle = COLOR_AQUA; ctx.fill();
      });
      nodeYs3.forEach(y => {
        ctx.beginPath(); ctx.arc(layer3X, y, 6, 0, Math.PI * 2); ctx.fillStyle = COLOR_PURPLE; ctx.fill();
      });

      // Machine Learning Loss Curve Graph (Right Side Box)
      const mlBoxX = width * 0.68;
      const mlBoxY = height * 0.15;
      const mlBoxW = width * 0.26;
      const mlBoxH = height * 0.32;

      ctx.strokeStyle = 'rgba(0, 229, 255, 0.25)';
      ctx.strokeRect(mlBoxX, mlBoxY, mlBoxW, mlBoxH);

      ctx.font = '600 10px "JetBrains Mono", monospace';
      ctx.fillStyle = COLOR_AQUA;
      ctx.fillText('[AI TRAINING LOSS DECAY]', mlBoxX + 10, mlBoxY + 16);

      ctx.beginPath();
      ctx.strokeStyle = COLOR_EMERALD;
      ctx.lineWidth = 1.8;
      for (let px = 0; px <= mlBoxW - 20; px += 4) {
        const norm = px / (mlBoxW - 20);
        const lossY = mlBoxY + mlBoxH - 12 - Math.exp(-norm * 3) * (mlBoxH * 0.65) + Math.sin(norm * 15 + time * 3) * 2;
        if (px === 0) ctx.moveTo(mlBoxX + 10 + px, lossY);
        else ctx.lineTo(mlBoxX + 10 + px, lossY);
      }
      ctx.stroke();

      ctx.restore();

      // 4. MATHEMATICAL FORMULAS STREAMING & CONVERGING (STAGE 1 & STAGE 2)
      ctx.save();
      const numFormulas = mathAndAiFormulas.length;
      mathAndAiFormulas.forEach((item, idx) => {
        const angle = (idx / numFormulas) * Math.PI * 2 + time * 0.2;

        let fx, fy, fAlpha, fScale;

        if (cycleProgress < 0.35) {
          // Stage 1: Floating across the screen workspace
          const orbitR = 140 + Math.sin(idx * 0.8 + time) * 60;
          fx = cx + Math.cos(angle) * (orbitR + width * 0.22);
          fy = cy + Math.sin(angle * 1.2) * (orbitR * 0.65 + height * 0.18);
          fAlpha = 0.85;
          fScale = 1;
        } else if (cycleProgress < 0.60) {
          // Stage 2: Convergence towards Central AI Synthesis Core
          const convProgress = (cycleProgress - 0.35) / 0.25;
          const targetDist = (1 - convProgress) * 250 + 20;
          fx = cx + Math.cos(angle) * targetDist;
          fy = cy + Math.sin(angle) * (targetDist * 0.6);
          fAlpha = 1 - convProgress * 0.4;
          fScale = 1.2 - convProgress * 0.4;
        } else {
          // Stage 3 & 4: Formulas transformed into candles
          fx = cx;
          fy = cy;
          fAlpha = 0;
          fScale = 0.5;
        }

        if (fAlpha > 0.05) {
          ctx.font = `700 ${Math.floor(13 * fScale)}px "JetBrains Mono", monospace, sans-serif`;
          ctx.fillStyle = item.category === 'AI_ML' || item.category === 'TRANSFORMER' ? COLOR_AQUA : COLOR_EMERALD;
          ctx.globalAlpha = fAlpha * 0.75;
          ctx.shadowColor = ctx.fillStyle;
          ctx.shadowBlur = 8;
          ctx.fillText(item.text, fx, fy);
          ctx.shadowBlur = 0;
        }
      });
      ctx.restore();

      // 5. STAGE 2 -> STAGE 3 CONVERGENCE SHOCKWAVES & AI TECH BADGES
      if (cycleProgress >= 0.35 && cycleProgress < 0.65) {
        ctx.save();
        const synthProgress = (cycleProgress - 0.35) / 0.30;
        const ringR = synthProgress * (width * 0.45);

        // Shockwave Synthesis Rings
        ctx.beginPath();
        ctx.arc(cx, cy, ringR, 0, Math.PI * 2);
        ctx.strokeStyle = COLOR_EMERALD;
        ctx.lineWidth = 2.5;
        ctx.globalAlpha = Math.max(0, 1 - synthProgress);
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(cx, cy, ringR * 0.7, 0, Math.PI * 2);
        ctx.strokeStyle = COLOR_AQUA;
        ctx.lineWidth = 1.8;
        ctx.globalAlpha = Math.max(0, 1 - synthProgress);
        ctx.stroke();

        // Central Tech Synthesis Badge
        ctx.font = '700 13px "JetBrains Mono", monospace';
        ctx.fillStyle = COLOR_WHITE;
        ctx.globalAlpha = Math.min(1, synthProgress * 3);
        ctx.textAlign = 'center';
        ctx.fillText('[SYNTHESIZING MATH + AI + ML MODELS -> GENERATING MARKET CANDLES]', cx, cy - 20);
        ctx.restore();
      }

      // 6. STAGE 3: CANDLESTICKS FORMED FROM COMBINED TECH (Time 0.60 -> 0.90)
      if (cycleProgress >= 0.55 && cycleProgress < 0.92) {
        ctx.save();
        const candleProgress = Math.min(1, (cycleProgress - 0.55) / 0.20);
        const candleAlpha = cycleProgress > 0.85 ? (0.92 - cycleProgress) / 0.07 : candleProgress;

        ctx.globalAlpha = Math.max(0, candleAlpha);

        // 18 Financial Candlesticks dynamically created across the curved screen workspace
        const candleCount = 18;
        const candleSpacing = (width * 0.78) / candleCount;
        const startX = width * 0.11;

        const candleData = [
          { base: 0.40, height: 35, green: true, gain: '+0.42%' },
          { base: 0.38, height: 28, green: true, gain: '+0.68%' },
          { base: 0.42, height: 18, green: false, gain: '-0.12%' },
          { base: 0.35, height: 45, green: true, gain: '+1.15%' },
          { base: 0.32, height: 50, green: true, gain: '+1.82%' },
          { base: 0.28, height: 25, green: false, gain: '-0.25%' },
          { base: 0.30, height: 42, green: true, gain: '+1.05%' },
          { base: 0.25, height: 55, green: true, gain: '+2.10%' },
          { base: 0.22, height: 38, green: true, gain: '+0.92%' },
          { base: 0.24, height: 20, green: false, gain: '-0.18%' },
          { base: 0.20, height: 62, green: true, gain: '+2.85%' },
          { base: 0.18, height: 48, green: true, gain: '+1.45%' },
          { base: 0.22, height: 22, green: false, gain: '-0.10%' },
          { base: 0.15, height: 70, green: true, gain: '+3.42%' },
          { base: 0.12, height: 58, green: true, gain: '+2.20%' },
          { base: 0.10, height: 65, green: true, gain: '+2.95%' },
          { base: 0.08, height: 78, green: true, gain: '+3.84%' },
          { base: 0.06, height: 85, green: true, gain: '+4.12%' }
        ];

        const topPoints = [];

        candleData.forEach((c, i) => {
          const candX = startX + i * candleSpacing;
          const targetY = height * c.base;
          const candH = c.height * candleProgress;
          const isGreen = c.green;
          const candColor = isGreen ? COLOR_EMERALD : COLOR_AQUA;

          ctx.shadowColor = candColor;
          ctx.shadowBlur = 12;

          // Upper & Lower Wicks
          const wickTop = 10;
          const wickBottom = 10;
          ctx.beginPath();
          ctx.moveTo(candX, targetY - wickTop);
          ctx.lineTo(candX, targetY + candH + wickBottom);
          ctx.strokeStyle = candColor;
          ctx.lineWidth = 2;
          ctx.stroke();

          // Candlestick Body Fill
          ctx.fillStyle = candColor;
          ctx.fillRect(candX - 6, targetY, 12, Math.max(4, candH));

          // Percentage Label above candle
          if (candleProgress > 0.6 && i % 2 === 0) {
            ctx.font = '700 10px "JetBrains Mono", monospace';
            ctx.fillStyle = COLOR_WHITE;
            ctx.fillText(c.gain, candX - 16, targetY - wickTop - 6);
          }

          topPoints.push({ x: candX, y: targetY - wickTop });
        });

        // Connected Neon Trendline through synthesized candles
        if (topPoints.length > 2 && candleProgress > 0.4) {
          ctx.beginPath();
          ctx.strokeStyle = COLOR_EMERALD;
          ctx.lineWidth = 2.5;
          topPoints.forEach((pt, pIdx) => {
            if (pIdx === 0) ctx.moveTo(pt.x, pt.y);
            else ctx.lineTo(pt.x, pt.y);
          });
          ctx.stroke();
        }

        // Synthesis Completion Text Badge
        if (candleProgress > 0.5) {
          ctx.font = '700 12px "JetBrains Mono", monospace';
          ctx.fillStyle = COLOR_EMERALD;
          ctx.textAlign = 'center';
          ctx.shadowBlur = 10;
          ctx.shadowColor = COLOR_EMERALD;
          ctx.fillText('✔ AI + ML + QUANT MATHEMATICS SYNTHESIS COMPLETE: MARKET INTELLIGENCE ENGAGED', cx, height - 65);
        }

        ctx.restore();
      }

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener('resize', updateDimensions);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div className="about-hero-quant-lab-bg">
      <canvas ref={canvasRef} className="about-hero-canvas" />
      <div className="about-hero-overlay-dark"></div>
    </div>
  );
};

// 2. PARTICLE INTELLIGENCE ANIMATION (Restored Old Video/Animation for "Engineering Market Intelligence" Section)
const ParticleIntelligenceAnimation = () => {
  const canvasRef = useRef(null);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId;
    let width = 0;
    let height = 0;

    const updateDimensions = () => {
      if (!canvas.parentElement) return;
      width = canvas.parentElement.clientWidth || 500;
      height = canvas.parentElement.clientHeight || 480;
      canvas.width = width;
      canvas.height = height;
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);

    const numParticles = 110;
    const particles = [];
    let centerX = width / 2;
    let centerY = height / 2;

    for (let i = 0; i < numParticles; i++) {
      particles.push({
        x: centerX,
        y: centerY,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        radius: Math.random() * 2 + 1,
        alpha: Math.random() * 0.6 + 0.4,
        pulseSpeed: 0.02 + Math.random() * 0.03,
        pulseAngle: Math.random() * Math.PI * 2,
        targetX: centerX,
        targetY: centerY
      });
    }

    const mathFormulas = [
      '∫ f(x)dx', 'd²P/dt²', 'Σ (x-μ)²', 'α = 3.84', 'β = 0.92', 
      'σ² = 0.04', '∇ × F', 'λ = 0.08ms', 'ΔP = +0.42%', 'E(R)', 
      'Sharpe = 3.84', 'Cov(X,Y)', 'P(A|B)', '∂V/∂t', 'dS/S'
    ];

    const numbersList = [
      '18,421.50', '0.0042', '+3.84σ', '101101', '0.08ms', 
      '+12.4%', '1,250 QTY', '42.80', 'Δ+0.85', 'σ=0.12'
    ];

    let blastItems = [];
    let shockwaves = [];
    let time = 0;
    let lastBlastTime = -10;

    const spawnBlastElements = (cx, cy) => {
      blastItems = [];
      shockwaves = [];

      for (let i = 0; i < 14; i++) {
        const angle = (i / 14) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
        const speed = 1.2 + Math.random() * 2.2;
        const text = mathFormulas[i % mathFormulas.length];
        blastItems.push({
          type: 'text',
          text,
          x: cx,
          y: cy,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          alpha: 1,
          size: Math.floor(10 + Math.random() * 5),
          color: Math.random() > 0.3 ? '#00ffd1' : '#00e5ff',
          life: 0
        });
      }

      for (let i = 0; i < 12; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 1.0 + Math.random() * 2.5;
        const text = numbersList[i % numbersList.length];
        blastItems.push({
          type: 'number',
          text,
          x: cx,
          y: cy,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          alpha: 1,
          size: Math.floor(9 + Math.random() * 4),
          color: '#ffffff',
          life: 0
        });
      }

      for (let i = 0; i < 12; i++) {
        const angle = (i / 12) * Math.PI * 2 + Math.random() * 0.2;
        const speed = 1.4 + Math.random() * 2.0;
        const isGreen = Math.random() > 0.3;
        const candleHeight = 14 + Math.random() * 18;
        const wickTop = 6 + Math.random() * 8;
        const wickBottom = 6 + Math.random() * 8;
        blastItems.push({
          type: 'candle',
          x: cx,
          y: cy,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          width: 6,
          height: candleHeight,
          wickTop,
          wickBottom,
          isGreen,
          alpha: 1,
          life: 0
        });
      }

      shockwaves.push({ r: 5, maxR: Math.max(cx, cy) * 0.9, alpha: 1, color: '#00ffd1' });
      shockwaves.push({ r: 2, maxR: Math.max(cx, cy) * 0.7, alpha: 0.8, color: '#00e5ff' });
    };

    const render = () => {
      time += 0.015;
      const w = canvas.width;
      const h = canvas.height;
      centerX = w / 2;
      centerY = h / 2;

      ctx.clearRect(0, 0, w, h);

      const cycle = (time % 12) / 12;

      if (cycle >= 0.20 && cycle < 0.25 && (time - lastBlastTime > 8)) {
        lastBlastTime = time;
        spawnBlastElements(centerX, centerY);
      }

      shockwaves.forEach((sw) => {
        sw.r += 4.5;
        sw.alpha *= 0.96;
        ctx.beginPath();
        ctx.arc(centerX, centerY, sw.r, 0, Math.PI * 2);
        ctx.strokeStyle = sw.color;
        ctx.lineWidth = 1.5;
        ctx.globalAlpha = Math.max(0, sw.alpha);
        ctx.stroke();
        ctx.globalAlpha = 1;
      });
      shockwaves = shockwaves.filter(sw => sw.alpha > 0.05);

      particles.forEach((p, i) => {
        if (cycle < 0.20) {
          const angle = (i / numParticles) * Math.PI * 2 + time * 0.8;
          const radius = 25 + Math.sin(i * 0.4 + time * 2) * 15;
          p.targetX = centerX + Math.cos(angle) * radius;
          p.targetY = centerY + Math.sin(angle) * radius;
        } else if (cycle < 0.50) {
          const angle = (i / numParticles) * Math.PI * 2 + (i % 5) * 0.2;
          const radius = 80 + (i / numParticles) * (w * 0.38);
          p.targetX = centerX + Math.cos(angle + time * 0.2) * radius;
          p.targetY = centerY + Math.sin(angle + time * 0.2) * radius;
        } else if (cycle < 0.80) {
          const cols = 11;
          const row = Math.floor(i / cols);
          const col = i % cols;
          const spacingX = (w * 0.72) / cols;
          const spacingY = (h * 0.72) / Math.ceil(numParticles / cols);
          p.targetX = centerX - (w * 0.36) + col * spacingX + Math.sin(time + row) * 8;
          p.targetY = centerY - (h * 0.36) + row * spacingY + Math.cos(time + col) * 8;
        } else {
          const angle = (i * 137.5 * Math.PI) / 180 + time * 0.4;
          const r = (1 - (cycle - 0.80) / 0.20) * (w * 0.35);
          p.targetX = centerX + Math.cos(angle) * r;
          p.targetY = centerY + Math.sin(angle) * r;
        }

        p.x += (p.targetX - p.x) * 0.04;
        p.y += (p.targetY - p.y) * 0.04;

        p.pulseAngle += p.pulseSpeed;
        const currentAlpha = p.alpha * (0.6 + 0.4 * Math.sin(p.pulseAngle));

        for (let j = i + 1; j < numParticles; j++) {
          const p2 = particles[j];
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const maxDist = 75;

          if (dist < maxDist) {
            const lineAlpha = (1 - dist / maxDist) * 0.35 * currentAlpha;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `rgba(0, 255, 209, ${lineAlpha})`;
            ctx.lineWidth = 0.75;
            ctx.stroke();
          }
        }

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 255, 209, ${currentAlpha})`;
        ctx.fill();
      });

      blastItems.forEach((item) => {
        item.x += item.vx * 0.97;
        item.y += item.vy * 0.97;
        item.life += 0.015;

        if (item.life < 0.1) {
          item.alpha = item.life / 0.1;
        } else if (item.life > 0.7) {
          item.alpha = Math.max(0, 1 - (item.life - 0.7) / 0.3);
        } else {
          item.alpha = 1;
        }

        ctx.globalAlpha = item.alpha;

        if (item.type === 'text' || item.type === 'number') {
          ctx.font = `600 ${item.size}px 'JetBrains Mono', monospace, sans-serif`;
          ctx.fillStyle = item.color;
          ctx.shadowColor = item.color;
          ctx.shadowBlur = 8;
          ctx.fillText(item.text, item.x, item.y);
          ctx.shadowBlur = 0;
        } else if (item.type === 'candle') {
          const isGreen = item.isGreen;
          const candleColor = isGreen ? '#00ffd1' : '#ff4d4d';
          const wickColor = isGreen ? '#00ffd1' : '#ff6b6b';

          ctx.shadowColor = candleColor;
          ctx.shadowBlur = 8;

          ctx.beginPath();
          ctx.moveTo(item.x, item.y - item.wickTop);
          ctx.lineTo(item.x, item.y + item.height + item.wickBottom);
          ctx.strokeStyle = wickColor;
          ctx.lineWidth = 1.2;
          ctx.stroke();

          ctx.fillStyle = candleColor;
          ctx.fillRect(item.x - item.width / 2, item.y, item.width, item.height);
          ctx.shadowBlur = 0;
        }

        ctx.globalAlpha = 1;
      });

      blastItems = blastItems.filter(item => item.life < 1.0);

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener('resize', updateDimensions);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div className="particle-intelligence-wrapper">
      <div className="particle-glow-backdrop"></div>
      <div className="particle-canvas-box">
        <canvas ref={canvasRef} className="particle-canvas" />
      </div>
    </div>
  );
};

const About = () => {
  const [imgError, setImgError] = React.useState(false);
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [1, 0.8, 0.6]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.95]);

  const springConfig = { stiffness: 100, damping: 30, restDelta: 0.001 };
  const scaleSpring = useSpring(scale, springConfig);

  return (
    <div className="page-container" ref={containerRef}>
      {/* Ultra Premium Hero */}
      <section className="about-hero-ultra">
        <motion.div 
          className="hero-cosmic-bg"
          style={{ y, opacity }}
        >
          <QuantLabCurvedBackground />
          <div className="cosmic-orb orb-mega-1"></div>
          <div className="cosmic-orb orb-mega-2"></div>
          <div className="cosmic-orb orb-mega-3"></div>
          <div className="star-field"></div>
        </motion.div>
        
        <motion.div 
          className="hero-content-ultra"
          style={{ scale: scaleSpring }}
        >
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.8, type: "spring", bounce: 0.4 }}
            className="hero-badge-ultra"
          >
            <Sparkles size={20} className="badge-sparkle" />
            Engineering Systematic Market Intelligence Since 2025
          </motion.div>

          <motion.h1 
            className="display-huge gradient-text"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            {aboutContent.title}
          </motion.h1>

          <motion.p 
            className="hero-description-ultra"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
          >
            {aboutContent.introduction}
          </motion.p>

          <motion.div 
            className="hero-metrics-bar"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.7 }}
          >
            {[
              { icon: <TrendingUp size={28} />, label: "Trading Infrastructure", value: "Institutional" },
              { icon: <Shield size={28} />, label: "SEBI • MCA • RBI Framework", value: "Compliance First" },
              { icon: <Zap size={28} />, label: "AI-Driven Research Systems", value: "24/7" }
            ].map((metric, i) => (
              <motion.div 
                key={i}
                className="metric-item-ultra"
                whileHover={{ y: -5, scale: 1.05 }}
                transition={{ duration: 0.2 }}
              >
                <div className="metric-icon-ultra">{metric.icon}</div>
                <div className="metric-text">
                  <span className="metric-value">{metric.value}</span>
                  <span className="metric-label">{metric.label}</span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div 
          className="scroll-indicator-ultra"
          animate={{ y: [0, 15, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          style={{ display: "none" }}
        >
          <div className="scroll-line-ultra"></div>
          <span className="scroll-text">Scroll to explore</span>
        </motion.div>
      </section>

      {/* Vision Mission Grid */}
      <section className="vision-mission-ultra">
        <div className="container-ultra">
          <motion.div
            className="vision-mission-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{
              hidden: { opacity: 0 },
              visible: { opacity: 1, transition: { staggerChildren: 0.3 } }
            }}
          >
            <motion.div 
              className="vision-card-ultra"
              variants={{
                hidden: { x: -100, opacity: 0 },
                visible: { x: 0, opacity: 1, transition: { duration: 0.8 } }
              }}
              whileHover={{ scale: 1.02, rotateY: 5 }}
            >
              <div className="card-glow-effect"></div>
              <div className="card-icon-mega">
                <Eye size={64} />
              </div>
              <h2 className="heading-1 card-title">Our Vision</h2>
              <p className="body-large card-desc">
                Advancing quantitative intelligence through research, technology, and systematic market infrastructure.
              </p>
              <div className="card-corner-glow top-left"></div>
              <div className="card-corner-glow bottom-right"></div>
            </motion.div>

            <motion.div 
              className="mission-card-ultra"
              variants={{
                hidden: { x: 100, opacity: 0 },
                visible: { x: 0, opacity: 1, transition: { duration: 0.8 } }
              }}
              whileHover={{ scale: 1.02, rotateY: -5 }}
            >
              <div className="card-glow-effect"></div>
              <div className="card-icon-mega mission-icon">
                <Rocket size={64} />
              </div>
              <h2 className="heading-1 card-title">Our Mission</h2>
              <p className="body-large card-desc">
                {aboutContent.mission}
              </p>
              <div className="card-corner-glow top-left"></div>
              <div className="card-corner-glow bottom-right"></div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Who We Are - Full Width Split */}
      <section className="who-we-are-ultra">
        <div className="who-container-ultra">
          <motion.div 
            className="who-grid-ultra"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{
              hidden: { opacity: 0 },
              visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
            }}
          >
            <motion.div 
              className="who-content"
              variants={{
                hidden: { opacity: 0, x: -80 },
                visible: { opacity: 1, x: 0, transition: { duration: 0.8 } }
              }}
            >
              <span className="section-label">Who We Are</span>
              <h2 className="display-medium">Engineering Market Intelligence</h2>
              <p className="body-large who-text">
                {aboutContent.whoWeAre}
              </p>

              <div className="stats-visual">
                {[
                  { icon: <TrendingUp />, label: "Proprietary Model", value: "100%" },
                  { icon: <Shield />, label: "Compliance", value: "SEBI" },
                  { icon: <Cpu />, label: "Engineering Culture", value: "Core" }
                ].map((stat, i) => (
                  <motion.div 
                    key={i}
                    className="stat-visual-item"
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1, type: "spring", bounce: 0.6 }}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    <div className="stat-icon-wrapper">{stat.icon}</div>
                    <span className="stat-value-text">{stat.value}</span>
                    <span className="stat-label-text">{stat.label}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div 
              className="who-visual"
              variants={{
                hidden: { opacity: 0, x: 80 },
                visible: { opacity: 1, x: 0, transition: { duration: 0.8 } }
              }}
            >
              <ParticleIntelligenceAnimation />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* New Section: Research Philosophy */}
      <section className="pillars-ultra-section">
        <div className="container-ultra">
          <motion.div
            className="section-header-ultra"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="section-label-center">Methodology</span>
            <h2 className="display-medium">Our Research Philosophy</h2>
            <p className="section-subtitle">Disciplined, data-driven principles driving algorithmic excellence</p>
          </motion.div>

          <motion.div 
            className="pillars-mega-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.15 }
              }
            }}
          >
            {[
              {
                title: "Mathematics",
                subtitle: "Rigorous Quantitative Modelling",
                desc: "Developing in-house mathematical models, stochastic calculus frameworks, and statistical arbitrage strategies tested against empirical market data.",
                icon: BarChart3,
                gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
              },
              {
                title: "Data",
                subtitle: "Evidence-Driven Decision Making",
                desc: "Processing high-density order book feeds, time-series history, and market microstructure analytics to form objective, hypothesis-driven statistical signals.",
                icon: Database,
                gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
              },
              {
                title: "Technology",
                subtitle: "Automation & Scalable Infrastructure",
                desc: "Building ultra-low-latency execution engines, automated research pipeline orchestration, and continuous AI model optimization.",
                icon: Zap,
                gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              },
              {
                title: "Risk Discipline",
                subtitle: "Capital Preservation & Controlled Execution",
                desc: "Enforcing real-time position controls, multi-layered automated circuit breakers, and algorithmic portfolio risk management across all market activities.",
                icon: Shield,
                gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
              }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={index}
                  className="pillar-mega-card"
                  variants={{
                    hidden: { y: 50, opacity: 0 },
                    visible: { y: 0, opacity: 1, transition: { duration: 0.6 } }
                  }}
                  whileHover={{ y: -12, scale: 1.02 }}
                >
                  <div className="pillar-bg-gradient" style={{ background: item.gradient }}></div>
                  <div className="pillar-icon-mega" style={{ background: item.gradient }}>
                    <Icon size={44} />
                  </div>
                  <span style={{ color: 'var(--brand-primary)', fontSize: '13px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '8px' }}>
                    {item.subtitle}
                  </span>
                  <h3 className="heading-2 pillar-title">{item.title}</h3>
                  <p className="body-medium pillar-desc">{item.desc}</p>
                  <div className="pillar-shine"></div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Core Pillars - Foundation */}
      <section className="pillars-ultra-section" style={{ paddingTop: '0' }}>
        <div className="container-ultra">
          <motion.div
            className="section-header-ultra"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="section-label-center">Foundation</span>
            <h2 className="display-medium">Our Core Pillars</h2>
            <p className="section-subtitle">Built on mathematical excellence, driven by systematic research</p>
          </motion.div>

          <motion.div 
            className="pillars-mega-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-150px" }}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.15,
                  delayChildren: 0.2
                }
              }
            }}
          >
            {aboutContent.sections.map((pillar, index) => {
              const gradients = [
                "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
              ];
              const icons = [Target, Cpu, Award, Shield];
              const Icon = icons[index];

              return (
                <motion.div
                  key={index}
                  className="pillar-mega-card"
                  variants={{
                    hidden: { y: 60, opacity: 0, scale: 0.8 },
                    visible: { 
                      y: 0, 
                      opacity: 1, 
                      scale: 1,
                      transition: { 
                        type: "spring", 
                        bounce: 0.4,
                        duration: 0.8 
                      } 
                    }
                  }}
                  whileHover={{ 
                    y: -15, 
                    scale: 1.03,
                    transition: { duration: 0.3 }
                  }}
                >
                  <div className="pillar-bg-gradient" style={{ background: gradients[index] }}></div>
                  <div className="pillar-icon-mega" style={{ background: gradients[index] }}>
                    <Icon size={48} />
                  </div>
                  <h3 className="heading-2 pillar-title">{pillar.title}</h3>
                  <p className="body-medium pillar-desc">{pillar.content}</p>
                  <div className="pillar-shine"></div>
                  <motion.div 
                    className="pillar-line-animated"
                    initial={{ width: 0 }}
                    whileInView={{ width: "100%" }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.2 + 0.5, duration: 0.8 }}
                  ></motion.div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* CEO Section - Premium */}
      <section className="ceo-ultra-section">
        <div className="container-ultra">
          <motion.div
            className="ceo-mega-card"
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="ceo-bg-pattern"></div>
            
            <div className="ceo-content-grid">
              <motion.div 
                className="ceo-avatar-section"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3, duration: 0.6 }}
              >
                <motion.div 
                  className="ceo-avatar-mega"
                  whileHover={{ scale: 1.05, rotate: 5 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="avatar-ring ring-1"></div>
                  <div className="avatar-ring ring-2"></div>
                  <div className="avatar-inner">
                    {aboutContent.ceoMessage.image && !imgError ? (
                      <img 
                        src={aboutContent.ceoMessage.image} 
                        alt={aboutContent.ceoMessage.name} 
                        className="w-full h-full object-cover rounded-full"
                        style={{ objectPosition: "center" }}
                        onError={() => setImgError(true)}
                      />
                    ) : (
                      <Users size={100} />
                    )}
                  </div>
                </motion.div>
                
                <h3 className="heading-1 ceo-name">{aboutContent.ceoMessage.name}</h3>
                <p className="body-medium ceo-title-text">{aboutContent.ceoMessage.designation}</p>
                
                <motion.a
                  href={aboutContent.ceoMessage.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ceo-linkedin-mega"
                  whileHover={{ scale: 1.05, boxShadow: "0 10px 40px rgba(0, 255, 209, 0.3)" }}
                  whileTap={{ scale: 0.95 }}
                >
                  Connect on LinkedIn
                  <ArrowRight size={18} />
                </motion.a>
              </motion.div>

              <div className="ceo-message-section">
                <div className="message-header">
                  <Heart size={40} className="message-icon" />
                  <h2 className="heading-1">Message from CEO</h2>
                </div>
                
                <div className="message-content">
                  <div className="quote-symbol">"</div>
                  <p className="body-large ceo-quote">
                    {aboutContent.ceoMessage.content}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values - Interactive */}
      <section className="values-ultra-section">
        <div className="container-ultra">
          <motion.div
            className="section-header-ultra"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-label-center">Our DNA</span>
            <h2 className="display-medium">Core Values</h2>
          </motion.div>

          <motion.div 
            className="values-mega-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.1 }
              }
            }}
          >
            {[
              { title: "Continuous Innovation", desc: "Advancing algorithmic research & models", icon: Zap, color: "#667eea" },
              { title: "Integrity", desc: "Uncompromising compliance & governance", icon: Shield, color: "#f5576c" },
              { title: "Research Excellence", desc: "Empirical mathematical rigor & validation", icon: Award, color: "#4facfe" },
              { title: "Execution Precision", desc: "Systematic risk-controlled execution", icon: Target, color: "#43e97b" }
            ].map((value, i) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={i}
                  className="value-mega-card"
                  variants={{
                    hidden: { scale: 0, rotate: -180 },
                    visible: { 
                      scale: 1, 
                      rotate: 0,
                      transition: { type: "spring", bounce: 0.6 }
                    }
                  }}
                  whileHover={{ 
                    scale: 1.1,
                    rotate: 5,
                    transition: { duration: 0.2 }
                  }}
                >
                  <div className="value-glow" style={{ background: `radial-gradient(circle, ${value.color}33, transparent)` }}></div>
                  <div className="value-icon-mega" style={{ color: value.color }}>
                    <Icon size={40} />
                  </div>
                  <h3 className="heading-3">{value.title}</h3>
                  <p className="body-small">{value.desc}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* New Section: Company Journey Timeline */}
      <section className="journey-ultra-section">
        <div className="container-ultra">
          <motion.div
            className="section-header-ultra"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="section-label-center">Evolution</span>
            <h2 className="display-medium">Company Journey</h2>
            <p className="section-subtitle">Building a globally respected quantitative research and proprietary trading institution</p>
          </motion.div>

          <motion.div 
            className="journey-timeline-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.15 }
              }
            }}
          >
            {[
              {
                year: "2025",
                title: "Incorporation & Governance",
                desc: "Quanentry Capital Private Limited incorporated with a foundational commitment to quantitative research and compliance-first corporate governance."
              },
              {
                year: "Infrastructure",
                title: "Research Pipeline Established",
                desc: "Built core high-density market data pipelines, time-series historical databases, and proprietary mathematical modeling frameworks."
              },
              {
                year: "Execution",
                title: "Institutional Trading Systems",
                desc: "Engineered ultra-low-latency execution engines, automated order routing, and real-time risk management controls."
              },
              {
                year: "AI Capabilities",
                title: "Advanced Research Expansion",
                desc: "Integrated deep learning, predictive signal processing, and market microstructure analytics into algorithmic models."
              },
              {
                year: "Long-Term Vision",
                title: "Global Institution Building",
                desc: "Building a globally respected quantitative research and proprietary trading institution anchored in research excellence."
              }
            ].map((milestone, idx) => (
              <motion.div
                key={idx}
                className="journey-card"
                variants={{
                  hidden: { y: 40, opacity: 0 },
                  visible: { y: 0, opacity: 1, transition: { duration: 0.6 } }
                }}
              >
                <span className="journey-badge">{milestone.year}</span>
                <h3 className="journey-card-title">{milestone.title}</h3>
                <p className="journey-card-desc">{milestone.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Legal - Elegant */}
      <section className="legal-ultra-section">
        <motion.div
          className="legal-mega-box"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.div 
            className="legal-icon-mega"
            animate={{ rotate: [0, 5, -5, 0] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
          >
            <CheckCircle size={64} />
          </motion.div>
          <h2 className="heading-1">Legal Compliance Statement</h2>
          <p className="body-large legal-text-mega">
            {aboutContent.legalStatement}
          </p>
        </motion.div>
      </section>

      {/* CTA - Compelling */}
      <section className="cta-ultra-section">
        <div className="cta-cosmic-bg">
          <div className="cta-orb orb-1"></div>
          <div className="cta-orb orb-2"></div>
        </div>
        <motion.div
          className="cta-mega-container"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <Sparkles size={48} className="cta-sparkle" />
          <h2 className="display-large">Engineer the Future of Systematic Trading</h2>
          <p className="body-large cta-text">
            Join a team focused on quantitative research, advanced technology, and disciplined execution.
          </p>
          <div className="cta-button-group">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link to="/careers" className="btn-primary btn-mega">
                Explore Opportunities
                <ArrowRight size={22} />
              </Link>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link to="/contact" className="btn-secondary btn-mega">
                Get in Touch
                <ArrowRight size={22} />
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default About;
